import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query, QueryGetVisitorGroupsForTenantArgs, VisitorGroup } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect, useMemo } from 'react';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';

/**
 * A group the add/edit user forms offer as an option (an ATLAS group to pick, or a preset role group). Only the fields
 * the forms read: the slim groups query selects the members' ids (`memberIds`, used for the "group is full" check),
 * not the members themselves.
 */
export type UserFormVisitorGroup = Pick<VisitorGroup, 'id' | 'name' | 'type' | 'maxNumberOfMembers' | 'memberIds'>;

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;

const filterByType = (visitorGroups: UserFormVisitorGroup[], type: string) =>
  visitorGroups.filter((visitorGroup) => visitorGroup.type === type);

/**
 * The groups the add/edit user forms need, split by group type: the ATLAS groups (group type `CO_CONTRACTORS`) to pick
 * from, and the preset groups behind the roles (group types `ADMINISTRATORS` and `INSTALLERS`). One unfiltered slim
 * query is shared with the other screens (same document, no variables), so the groups come from the cache and refresh
 * in the background.
 */
const useUserFormVisitorGroups = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const { loading, error, data } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (error) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(error);
    }
  }, [error]);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  useRegisterBackgroundRefetch(!!data && loading);

  const visitorGroups = useMemo(() => {
    if (!data) return undefined;
    const allVisitorGroups: UserFormVisitorGroup[] = data.getVisitorGroupsForTenant;
    return {
      coContractors: filterByType(allVisitorGroups, VisitorGroupType.CoContractors),
      administratorsVisitorGroups: filterByType(allVisitorGroups, VisitorGroupType.Administrators),
      installersVisitorGroups: filterByType(allVisitorGroups, VisitorGroupType.Installers)
    };
  }, [data]);

  return {
    coContractors: visitorGroups?.coContractors ?? [],
    administratorsVisitorGroups: visitorGroups?.administratorsVisitorGroups,
    installersVisitorGroups: visitorGroups?.installersVisitorGroups,
    // Only the first load counts: the forms reset when loading ends, so a background refetch must not toggle it.
    visitorGroupsLoading: loading && !data
  };
};

export default useUserFormVisitorGroups;
